import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { FC } from 'react';
// we are using DraggableCore here instead of Draggable because Draggable will set
// the transform style and that will create new stacking context and will break react-mentions positioning,
// so we have to set position using left/top instead of transform: translate(...).
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import { $hasCname, $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { PortalStyleConfig, ThemeColorSchemas, UnleashFeatures } from '@nx-frontend/shared/types';
import { trackClickRelated } from './analytics';
import { ChatbotChat } from './chatbot-chat';
import { ChatbotContext } from './chatbot-context';
import { ChatbotHeader } from './chatbot-header';
import { ChatbotHistory } from './chatbot-history';
import { ChatbotInput } from './chatbot-input';
import { ChatbotMainPage } from './chatbot-main';
import {
  $activeThreadId,
  $aiPortalVisitorDetails,
  $aiThreads,
  $writingMessage,
  abortMessageEvent,
  clearSystemThreadEvent,
  onMessageResolvedEvent,
  sendMessageEvent,
  setActiveThreadIdEvent,
  setEditMessageIdEvent,
} from './store';
import { ChatbotMessage, PromptType, UserLocation } from './types';
import { isTextInputNode } from './util';

interface ChatbotAssistantModeProps {
  showInfoMenu: boolean;
  isDev: boolean;
  onClose: () => void;
  themeColors: ThemeColorSchemas;
  themeStyle: PortalStyleConfig;
  onUpdateWorkspaceIndexing: (value: boolean) => void;
  updateThreadMessages: (threadId: string) => void;
  onThreadDelete: (threadId: string) => void;
  onAddMessageToPage: (text: string, threadId: string) => void;
  onFeedback: (threadId: string, message: ChatbotMessage) => void;
  allowCreatePage?: boolean;
  updateThreads: () => void;
  onShowUpgradePopup: () => void;
  onShowAddonPopup: () => void;
  currentLocation: UserLocation;
  useWeb: boolean;
  onSwitchUseWeb: (value: boolean) => void;
}

export const ChatbotAssistantMode: FC<ChatbotAssistantModeProps> = ({
  showInfoMenu,
  isDev,
  onClose,
  themeColors,
  themeStyle,
  onUpdateWorkspaceIndexing,
  updateThreadMessages,
  onThreadDelete,
  onAddMessageToPage,
  onFeedback,
  allowCreatePage,
  updateThreads,
  onShowUpgradePopup,
  onShowAddonPopup,
  currentLocation,
  useWeb,
  onSwitchUseWeb,
}) => {
  const [
    orgInfo,
    hasCname,
    aiThreads,
    activeThreadId,
    setActiveThread,
    writingMessage,
    clearSystemThread,
    abortMessage,
    aiPortalVisitorDetails,
    unleashFlags,
    onMessageChunkResolved,
    sendMessage,
    setEditMessageId,
  ] = useUnit([
    $orgInfo,
    $hasCname,
    $aiThreads,
    $activeThreadId,
    setActiveThreadIdEvent,
    $writingMessage,
    clearSystemThreadEvent,
    abortMessageEvent,
    $aiPortalVisitorDetails,
    $unleashFlags,
    onMessageResolvedEvent,
    sendMessageEvent,
    setEditMessageIdEvent,
  ]);

  const [showHistory, setShowHistory] = useState(false);
  const [isChatFromHistory, setIsChatFromHistory] = useState(false);
  const [showLimitBanner, setShowLimitBanner] = useState(true);

  const chatbotInputRef = useRef<HTMLTextAreaElement | null>(null);

  const aiContext = useContext(ChatbotContext);

  const t = useTranslations('features');

  const onAbortMessage = useCallback(
    (messageId: string, text: string) => {
      abortMessage({
        sessionId: aiContext.sessionId,
        apiHost: aiContext.apiHost,
        workspaceId: aiContext.workspaceId,
        messageId,
        text,
      });
    },
    [abortMessage, aiContext.apiHost, aiContext.sessionId, aiContext.workspaceId]
  );

  const onSend = useCallback(
    (message: string, promptType?: PromptType) => {
      const abortController = new AbortController();

      promptType = promptType || 'userEntered';

      sendMessage({
        threadId: activeThreadId,
        sessionId: aiContext.sessionId,
        apiHost: aiContext.apiHostCreateMessage,
        workspaceId: aiContext.workspaceId,
        portalId: aiContext.portalId,
        portalVisitorId: aiContext?.role?.role === 'visitor' ? aiPortalVisitorDetails?.portalVisitorId : undefined,
        text: message,
        onMessageChunkResolved,
        abortController,
        promptType,
        useWeb,
        currentLocationType: currentLocation.type,
        currentLocationId: currentLocation.id,
        currentLocationTitle: currentLocation.title,
        enableDetectUserMessageContext:
          !aiContext.portalId && unleashFlags[UnleashFeatures.AI_ASSISTANT_DETECT_USER_MESSAGE_CONTEXT],
      });
    },
    [
      activeThreadId,
      sendMessage,
      aiContext.sessionId,
      aiContext.apiHostCreateMessage,
      aiContext.workspaceId,
      aiContext.portalId,
      onMessageChunkResolved,
      useWeb,
      currentLocation.id,
      currentLocation.type,
      currentLocation.title,
      aiPortalVisitorDetails,
      unleashFlags,
      aiContext?.role?.role,
    ]
  );

  const aiAssistantName = useMemo(() => {
    if (aiThreads.preferences.name) {
      return aiThreads.preferences.name;
    }
    return `${hasCname && orgInfo.title ? orgInfo.title : 'FuseBase'} Assistant`;
  }, [hasCname, orgInfo.title, aiThreads.preferences.name]);

  const aiIsWritingMessageInCurrentThread = useMemo(() => {
    return (
      Boolean(aiThreads.tmpMessages?.length) ||
      (activeThreadId !== null && Boolean(aiThreads.threads[activeThreadId]?.isWriting))
    );
  }, [activeThreadId, aiThreads.threads, aiThreads.tmpMessages?.length]);

  useEffect(() => {
    if (!aiIsWritingMessageInCurrentThread) {
      // Need to focus on the assistant input box on assistant popup open and when AI finishes writing.
      // However, we don't want to focus on the input box if the user is typing in another editable element.
      if (!isTextInputNode(document.activeElement as HTMLElement)) {
        if (chatbotInputRef.current) {
          chatbotInputRef.current.focus();
        }
      }
    }
  }, [aiIsWritingMessageInCurrentThread]);

  const onHeaderBackClick = useCallback(() => {
    clearSystemThread();
    if (showHistory) {
      setShowHistory(false);
    } else {
      setActiveThread(null);
      if (isChatFromHistory) {
        setShowHistory(true);
        setIsChatFromHistory(false);
      }
    }
    if (aiIsWritingMessageInCurrentThread && writingMessage?.id && writingMessage?.message) {
      onAbortMessage(writingMessage.id, writingMessage.message);
    }
  }, [
    aiIsWritingMessageInCurrentThread,
    isChatFromHistory,
    onAbortMessage,
    setActiveThread,
    showHistory,
    writingMessage?.id,
    writingMessage?.message,
    clearSystemThread,
  ]);

  const onSelectThread = useCallback(
    (id: string) => {
      setActiveThread(id);
      if (showHistory) {
        setIsChatFromHistory(true);
        setShowHistory(false);
      }
      if (aiThreads.threads[id] && (!aiThreads.threads[id].messages || !aiThreads.threads[id].messages.length)) {
        updateThreadMessages(id);
      }

      trackClickRelated('historyItem');
    },
    [setActiveThread, showHistory, aiThreads.threads, updateThreadMessages]
  );

  const onThreadDeleteCb = useCallback(
    (id: string) => {
      onThreadDelete?.(id);
    },
    [onThreadDelete]
  );

  const onSendMessage = useCallback(
    (value: string, promptType?: PromptType) => {
      if (promptType === 'promptSuggestion') {
        trackClickRelated('promptSuggestion');
      }

      onSend?.(value, promptType);
    },
    [onSend]
  );

  const onEndEditMessage = () => {
    if (!activeThreadId) {
      return;
    }
    setEditMessageId({ threadId: activeThreadId, messageId: null });
  };

  const aiAssistantCustomLogoUrl = useMemo(() => {
    if (!aiThreads.preferences.logoStoredFileUUID) {
      return null;
    }
    return `/box/file/${aiThreads.preferences.logoStoredFileUUID}?width=64`;
  }, [aiThreads.preferences.logoStoredFileUUID]);

  const onViewHistoryClick = useCallback(() => {
    setShowHistory(true);
    updateThreads();
  }, [updateThreads]);

  const onCloseLimitBanner = useCallback(() => {
    setShowLimitBanner(false);
  }, []);

  let inputPlaceholderText = activeThreadId
    ? t('ai-helper.tell_what_to_do_next')
    : t('ai-helper.start_a_chat') + ' ' + t('ai-helper.use_to_add_context');
  if (aiContext.portalId) {
    inputPlaceholderText = t('ai-helper.ask_a_question');
    if (!aiContext.isVisitor) {
      inputPlaceholderText += ' ' + t('ai-helper.use_to_add_context');
    }
  }

  return (
    <>
      {/* preload ai assistant logo for better UX */}
      {aiAssistantCustomLogoUrl && <link rel="preload" href={aiAssistantCustomLogoUrl} as="image" />}
      {/*Heading */}
      <ChatbotHeader
        showInfoMenu={showInfoMenu}
        title={
          showHistory
            ? t('ai-helper.chat_history')
            : activeThreadId || (aiThreads.tmpMessages && aiThreads.tmpMessages.length)
            ? activeThreadId && aiThreads.threads[activeThreadId].title
              ? aiThreads.threads[activeThreadId].title ?? t('ai-helper.untitled')
              : t('ai-helper.untitled')
            : aiAssistantName
        }
        onBack={
          showHistory || activeThreadId || (aiThreads.tmpMessages && aiThreads.tmpMessages.length)
            ? onHeaderBackClick
            : undefined
        }
        onClose={onClose}
        themeColors={themeColors}
        themeStyle={themeStyle}
        onUpdateWorkspaceIndexing={onUpdateWorkspaceIndexing}
      />
      {/*Chat Container */}
      {showHistory ? (
        <ChatbotHistory
          onSelectThread={onSelectThread}
          onThreadDelete={onThreadDeleteCb}
          themeColors={themeColors}
          themeStyle={themeStyle}
        />
      ) : activeThreadId || (aiThreads.tmpMessages && aiThreads.tmpMessages.length) ? (
        <ChatbotChat
          threadId={activeThreadId}
          onSendMessage={onSendMessage}
          themeColors={themeColors}
          themeStyle={themeStyle}
          aiName={aiAssistantName}
          aiCustomLogoUrl={aiAssistantCustomLogoUrl}
          isWriting={aiIsWritingMessageInCurrentThread}
          onAddMessageToPage={onAddMessageToPage}
          onFeedback={onFeedback}
          allowCreatePage={allowCreatePage}
        />
      ) : (
        <ChatbotMainPage
          onSendMessage={onSendMessage}
          onViewHistoryClick={onViewHistoryClick}
          themeColors={themeColors}
          themeStyle={themeStyle}
          onSelectThread={onSelectThread}
          onThreadDelete={onThreadDeleteCb}
          showLimitBanner={showLimitBanner}
          onCloseLimitBanner={onCloseLimitBanner}
          onShowUpgradePopup={onShowUpgradePopup}
          onShowAddonPopup={onShowAddonPopup}
          currentLocation={currentLocation}
        />
      )}
      {/*Input box  */}
      {!showHistory ? (
        <ChatbotInput
          inputRef={chatbotInputRef}
          themeColors={themeColors}
          themeStyle={themeStyle}
          onSend={onSendMessage}
          onEndEditMessage={onEndEditMessage}
          isWriting={aiIsWritingMessageInCurrentThread}
          isCreatingPage={
            aiIsWritingMessageInCurrentThread &&
            activeThreadId &&
            aiThreads.threads[activeThreadId].messages &&
            writingMessage?.system
              ? true
              : false
          }
          useWeb={useWeb}
          onSwitchUseWeb={onSwitchUseWeb}
          onAbortMessage={onAbortMessage}
          placeholder={inputPlaceholderText}
        />
      ) : null}
    </>
  );
};
